import axios from 'axios';
import { generateHeaders } from '../../utils/APIUtils';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { login, logout } from './auth';
import { Reject } from '../Reject';
import { handleApiError } from '../handleApiError';

interface UserState {
  storingHome: boolean;
  homeStoredError: boolean;
  homeStoredSuccess: boolean;
  latitude: number;
  longitude: number;
  zoom: number;
}

const initialState: UserState = {
    storingHome: false,
    homeStoredError: false,
    homeStoredSuccess: false,
    latitude: undefined,
    longitude: undefined,
    zoom: undefined,
};

interface StoreHomeParams {
  latitude: number;
  longitude: number;
  zoom: number;
}

type StoreHomeResponse = {
    homeSet: boolean;
    latitude: string;
    longitude: string;
    zoom: string;
};

export const storeHome = createAsyncThunk<
    StoreHomeResponse,
    StoreHomeParams,
    Reject
>(
    'storeHome',
    async (params: StoreHomeParams, thunkAPI) => {
        const { auth } = thunkAPI.getState() as RootState;
        const headers = generateHeaders(auth.jwt);

        if (params.longitude && params.latitude) {
            try {
                const response = await axios.put<StoreHomeResponse>(`${process.env.REACT_APP_API_URL}/user/home`, { ...params }, { headers });
                return { ...response.data };
            } catch (error) {
                const apiError = handleApiError(error);
                return thunkAPI.rejectWithValue(apiError);
            }
        }
    }
);

const homeSlice = createSlice({
    name: 'home',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(storeHome.pending, (state) => {
                state.storingHome = true;
                state.homeStoredError = false;
                state.homeStoredSuccess = false;
            })
            .addCase(storeHome.rejected, (state) => {
                state.storingHome = false;
                state.homeStoredError = true;
                state.homeStoredSuccess = false;
            })
            .addCase(storeHome.fulfilled, (state, action) => {
                state.storingHome = false;
                state.homeStoredError = false;
                state.homeStoredSuccess = true;
                state.latitude = parseFloat(action.payload.latitude);
                state.longitude = parseFloat(action.payload.longitude);
                state.zoom = parseFloat(action.payload.zoom);
            })
            .addCase(login.fulfilled, (state, action) => {
                const { data } = action.payload.tokenData;
                state.latitude = parseFloat(data.latitude);
                state.longitude = parseFloat(data.longitude);
                state.zoom = parseFloat(data.zoom);
            })
            .addCase(logout.toString(), () => ({ ...initialState }));
    },
});

export default homeSlice.reducer;
